import { useEffect, useMemo, useState } from 'react';
import type { TicketAttachment, TicketMessage } from '../../types/tickets';
import { formatTicketDateTime } from '../../lib/ticketComposeHelpers';
import {
  getTicketAttachmentUrl,
  regularFileAttachments,
} from '../../lib/tickets';

type Row = {
  att: TicketAttachment;
  message: TicketMessage;
  sender: string;
};

type Props = {
  messages: TicketMessage[];
  attachmentsByMessage: Record<string, TicketAttachment[]>;
  loading?: boolean;
};

function senderLabel(msg: TicketMessage): string {
  if (msg.direction === 'outbound') {
    return msg.created_by_email || msg.from_email || 'Agent';
  }
  if (msg.direction === 'note') return 'Internal note';
  if (msg.from_name) return `${msg.from_name} <${msg.from_email}>`;
  return msg.from_email || 'Customer';
}

async function openAttachment(path: string) {
  const url = await getTicketAttachmentUrl(path);
  window.open(url, '_blank', 'noopener,noreferrer');
}

export function TicketAttachmentsPanel({ messages, attachmentsByMessage, loading }: Props) {
  const [busyId, setBusyId] = useState<string | null>(null);

  const rows = useMemo(() => {
    const out: Row[] = [];
    for (const msg of messages) {
      const atts = regularFileAttachments(attachmentsByMessage[msg.id] ?? [], msg.body_html || '');
      for (const att of atts) {
        out.push({ att, message: msg, sender: senderLabel(msg) });
      }
    }
    out.sort(
      (a, b) =>
        new Date(b.message.sent_at).getTime() - new Date(a.message.sent_at).getTime(),
    );
    return out;
  }, [messages, attachmentsByMessage]);

  useEffect(() => {
    setBusyId(null);
  }, [rows.length]);

  if (loading && rows.length === 0) {
    return <p className="ticket-attachments-panel__empty">Loading attachments…</p>;
  }

  if (rows.length === 0) {
    return <p className="ticket-attachments-panel__empty">No attachments in this conversation.</p>;
  }

  return (
    <div className="ticket-attachments-panel">
      <table className="ticket-attachments-panel__table">
        <thead>
          <tr>
            <th>File</th>
            <th>Sent</th>
            <th>From</th>
            <th>Size</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ att, message, sender }) => (
            <tr key={att.id}>
              <td>
                <button
                  type="button"
                  className="ticket-attachments-panel__file"
                  disabled={busyId === att.id}
                  onClick={() => {
                    setBusyId(att.id);
                    void openAttachment(att.storage_path).finally(() => setBusyId(null));
                  }}
                >
                  {att.file_name}
                </button>
              </td>
              <td>
                <time dateTime={message.sent_at}>{formatTicketDateTime(message.sent_at)}</time>
              </td>
              <td title={sender}>{sender}</td>
              <td>
                {att.size_bytes > 0 ? `${Math.max(1, Math.round(att.size_bytes / 1024))} KB` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
